import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState } from '@/components/ui/states';
import {
  ConsentStatus,
  ContactChannel,
  HUMAN_CONSENT_SOURCES,
  apiError,
  type ChannelConsent,
  type ConsentEvent,
} from '../lib';

/**
 * ADR-0010 §4 — per-channel consent, never implied by a phone number on file.
 * `status: null` (no row) renders as "not asked" and NOT as a fourth colour of
 * "no" — it is treated as `DENIED` by every send path, but the dashboard still
 * has to say the venue never actually asked, because that is a different fact
 * for a manager deciding whether to go ask.
 */
export default function ConsentTab({
  customerId,
  consent,
  history,
  onChanged,
}: {
  customerId: string;
  consent: ChannelConsent[];
  history: ConsentEvent[];
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canRecord = can('customers.consent');

  const [channel, setChannel] = useState<ContactChannel>(ContactChannel.SMS);
  const [status, setStatus] = useState<ConsentStatus>(ConsentStatus.GRANTED);
  const [source, setSource] = useState(HUMAN_CONSENT_SOURCES[0]);
  const [evidence, setEvidence] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/customers/${customerId}/consent`, {
        channel,
        status,
        source,
        evidence: evidence.trim() || undefined,
      });
      toast.success(t('crm.consent.recorded'));
      setEvidence('');
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('crm.consent.recordFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>{t('crm.consent.title')}</CardTitle>
          <p className="text-xs text-ink-muted">{t('crm.consent.subtitle')}</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {consent.map((c) => (
            <div key={c.channel} className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm">
              <div>
                <p className="font-medium">{t(`crm.consent.channel.${c.channel}`)}</p>
                {c.status ? (
                  <p className="text-xs text-ink-muted">
                    {t('crm.consent.capturedOn', {
                      date: c.capturedAt ? formatDateTime(c.capturedAt, i18n.language) : '—',
                      source: t(`crm.consent.source.${c.source}`),
                    })}
                  </p>
                ) : (
                  <p className="text-xs text-ink-muted">{t('crm.consent.neverAsked')}</p>
                )}
              </div>
              <Badge tone={c.status === ConsentStatus.GRANTED ? 'success' : c.status ? 'danger' : 'neutral'}>
                {c.status ? t(`crm.consent.status.${c.status}`) : t('crm.consent.unknown')}
              </Badge>
            </div>
          ))}

          {canRecord && (
            <div className="space-y-2 rounded-lg border p-3">
              <h4 className="text-sm font-semibold">{t('crm.consent.recordTitle')}</h4>
              <div className="grid gap-2 sm:grid-cols-2">
                <Field label={t('crm.segments.consentChannel')}>
                  <Select value={channel} onChange={(e) => setChannel(e.target.value as ContactChannel)}>
                    {Object.values(ContactChannel).map((c) => (
                      <option key={c} value={c}>
                        {t(`crm.consent.channel.${c}`)}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label={t('crm.consent.statusLabel')}>
                  <Select value={status} onChange={(e) => setStatus(e.target.value as ConsentStatus)}>
                    {Object.values(ConsentStatus).map((s) => (
                      <option key={s} value={s}>
                        {t(`crm.consent.status.${s}`)}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
              <Field label={t('crm.consent.sourceLabel')}>
                <Select value={source} onChange={(e) => setSource(e.target.value as typeof source)}>
                  {HUMAN_CONSENT_SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {t(`crm.consent.source.${s}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('crm.consent.evidenceLabel')} hint={t('crm.consent.evidenceHint')}>
                <Input value={evidence} onChange={(e) => setEvidence(e.target.value)} />
              </Field>
              <Button className="w-full" onClick={() => void submit()} disabled={saving}>
                {t('common.confirm')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('crm.consent.historyTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {history.length === 0 ? (
            <EmptyState title={t('crm.consent.noHistory')} />
          ) : (
            <ul className="divide-y text-sm">
              {history.map((event) => (
                <li key={event.id} className="space-y-0.5 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{t(`crm.consent.channel.${event.channel}`)}</span>
                    <Badge tone={event.status === ConsentStatus.GRANTED ? 'success' : 'danger'}>
                      {t(`crm.consent.status.${event.status}`)}
                    </Badge>
                  </div>
                  <p className="text-xs text-ink-muted">
                    {t('crm.consent.eventLine', {
                      date: formatDateTime(event.createdAt, i18n.language),
                      source: t(`crm.consent.source.${event.source}`),
                      previous: event.previousStatus
                        ? t(`crm.consent.status.${event.previousStatus}`)
                        : t('crm.consent.unknown'),
                    })}
                  </p>
                  {event.evidence && <p className="text-xs italic text-ink-muted">"{event.evidence}"</p>}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
